import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NextPage from "../components/NextPage";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import { Butterfly } from "../components/Butterflies";
import { getMeetTheHerdPage } from "../lib/api";
import { playSound } from "../lib/audio";
import { safeHtml } from "../lib/safeHtml";

const DEFAULT_PAGE = {
  seo_title: "Meet the Herd",
  seo_description:
    "Every Lil Saltie\u2122 has a story to tell, but together they share a greater purpose \u2014 slow down, notice the gentle Saltie Winks\u2122, and grow closer with every page.",
  hero_subtitle: "Where the pasture opens wide and every heart finds a home.",
  scripture_text:
    "For every animal of the forest is Mine, and the cattle on a thousand hills.",
  scripture_reference: "Psalm 50:10",
  body_html:
    "<p>Every Lil Saltie\u2122 has a story to tell, but together they share a greater purpose. Their adventures invite us to slow down, gather together, pause at life\u2019s little \u201csalt blocks,\u201d notice the gentle Saltie Winks\u2122 God places along the way, and grow a little closer to Him\u2014and to each other\u2014with every page we turn.</p>",
  closing_script_line: "Let\u2019s meet the Lil Salties\u2122.",
  more_than_heading: "More Than a Herd",
  more_than_intro_body:
    "At The Salty Moo, our animals remind us to pause and notice God in the ordinary moments.",
  more_than_saying_bray: "Hear a Bray. Pray.",
  more_than_saying_moo: "Hear a Moo. Praise, Too.",
  more_than_donkey_line: "The donkeys remind us to talk to God.",
  more_than_cow_line: "The cows remind us to praise God.",
  more_than_outro_body:
    "As you meet the herd, you will discover how these two simple reminders became part of life at The Salty Moo.",
  more_than_bray_link_label: "Meet the Saltie Brays",
  more_than_moo_link_label: "Meet the Saltie Moos",
  next_button_label: "Meet the Saltie Brays\u2122",
};

export default function MeetTheHerd() {
  const [page, setPage] = useState(DEFAULT_PAGE);
  useEffect(() => {
    getMeetTheHerdPage()
      .then((p) => setPage({ ...DEFAULT_PAGE, ...(p || {}) }))
      .catch(() => {});
  }, []);

  return (
    <div className="parchment min-h-screen" data-testid="meet-the-herd-page">
      <SEO
        title={page.seo_title}
        description={page.seo_description}
        path="/meet-the-herd"
      />

      <ChapterHero
        path="/meet-the-herd"
        image="/assets/heroes/meet-the-herd.webp"
        subtitle={page.hero_subtitle}
        position="top-center"
      />

      <div className="max-w-3xl mx-auto px-6 -mt-8 relative z-10 text-center pb-8">
        <div
          className="mx-auto max-w-2xl px-8 py-10 rounded-md parchment-deep border border-[#E7DFD5] page-shadow"
        >
          <blockquote className="font-display italic text-2xl md:text-3xl text-[#2C2724] leading-relaxed">
            &ldquo;{page.scripture_text}&rdquo;
          </blockquote>
          <p className="mt-3 text-base uppercase tracking-[0.25em] text-[#4B3A2F]">
            {page.scripture_reference}
          </p>
          <div className="gold-rule mx-auto my-6" />
          <div
            className="font-body text-lg md:text-xl text-[#2C2724]/85 leading-relaxed prose-winks"
            dangerouslySetInnerHTML={safeHtml(page.body_html)}
          />
          <p className="font-script text-3xl md:text-4xl text-[#4B3A2F] mt-6">
            {page.closing_script_line}
          </p>
        </div>
      </div>

      {/* ── More Than a Herd ─────────────────────────────────────── */}
      <section
        data-testid="more-than-a-herd"
        className="relative max-w-5xl mx-auto px-6 pt-6 pb-20"
      >
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="mx-auto mb-3 opacity-90">
            <Butterfly />
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[#4B3A2F] mb-4">
            {page.more_than_heading}
          </h2>
          <p className="font-body text-lg md:text-xl text-[#2C2724]/85 leading-relaxed">
            {page.more_than_intro_body}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-6">
          {/* Bray companion feature */}
          <Link
            to="/saltie-brays"
            data-testid="more-than-bray-card"
            className="group block relative rounded-md border border-[#C8A876]/50 bg-[#F9F1E1] p-7 text-center transition transform hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#B08A5A]"
            style={{
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.6) inset, 0 -1px 0 rgba(0,0,0,0.04) inset, 0 18px 28px -18px rgba(75,58,47,0.35), 0 2px 0 rgba(75,58,47,0.15)",
            }}
          >
            {/* Tappable Bonnie — plays her bray without navigating.
                stopPropagation/preventDefault keep the outer <Link>
                from firing so the sound plays alone. */}
            <button
              type="button"
              data-testid="more-than-bray-audio-btn"
              aria-label="Tap to hear Bonnie bray"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                playSound("/audio/saltie-bray.mp3", 0.9, { singleton: true });
              }}
              className="mx-auto mb-1 flex items-center justify-center rounded-full transition transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#B08A5A]"
              style={{ padding: 0, background: "transparent" }}
            >
              <img
                src="/assets/photos/bonnie.webp"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                alt=""
                aria-hidden
                className="w-16 h-16 rounded-full object-cover border-2 border-[#C8A876]/40"
              />
            </button>
            <p className="mb-3 text-[11px] uppercase tracking-[0.28em] text-[#A87149] italic">
              🫏 Tap Bonnie to hear her bray!
            </p>
            <p className="font-display italic text-2xl md:text-3xl text-[#4B3A2F] leading-snug">
              {page.more_than_saying_bray}
            </p>
            <p className="mt-3 font-body text-base text-[#2C2724]/85 leading-relaxed">
              {page.more_than_donkey_line}
            </p>
            <span className="inline-block mt-5 text-xs uppercase tracking-[0.35em] text-[#A87149] border-b border-[#A87149]/50 group-hover:text-[#4B3A2F] group-hover:border-[#4B3A2F]">
              {page.more_than_bray_link_label} →
            </span>
          </Link>

          {/* Moo companion feature */}
          <Link
            to="/saltie-moos"
            data-testid="more-than-moo-card"
            className="group block relative rounded-md border border-[#7E9268]/50 bg-[#F9F1E1] p-7 text-center transition transform hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#7E9268]"
            style={{
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.6) inset, 0 -1px 0 rgba(0,0,0,0.04) inset, 0 18px 28px -18px rgba(75,58,47,0.35), 0 2px 0 rgba(75,58,47,0.15)",
            }}
          >
            <button
              type="button"
              data-testid="more-than-moo-audio-btn"
              aria-label="Tap to hear Winnie moo"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                playSound("/audio/saltie-moo.mp3", 0.9, { singleton: true });
              }}
              className="mx-auto mb-1 flex items-center justify-center rounded-full transition transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#7E9268]"
              style={{ padding: 0, background: "transparent" }}
            >
              <img
                src="/assets/photos/winnie-the-moo.webp"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                alt=""
                aria-hidden
                className="w-16 h-16 rounded-full object-cover border-2 border-[#7E9268]/40"
              />
            </button>
            <p className="mb-3 text-[11px] uppercase tracking-[0.28em] text-[#7E9268] italic">
              🐮 Tap Winnie to hear her moo!
            </p>
            <p className="font-display italic text-2xl md:text-3xl text-[#4B3A2F] leading-snug">
              {page.more_than_saying_moo}
            </p>
            <p className="mt-3 font-body text-base text-[#2C2724]/85 leading-relaxed">
              {page.more_than_cow_line}
            </p>
            <span className="inline-block mt-5 text-xs uppercase tracking-[0.35em] text-[#7E9268] border-b border-[#7E9268]/60 group-hover:text-[#4B3A2F] group-hover:border-[#4B3A2F]">
              {page.more_than_moo_link_label} →
            </span>
          </Link>
        </div>

        <p className="mt-10 text-center font-body italic text-[#2C2724]/80 leading-relaxed max-w-2xl mx-auto">
          {page.more_than_outro_body}
        </p>
      </section>

      <NextPage to="/saltie-brays" label={page.next_button_label} />
    </div>
  );
}

